/**
 * The Stop and Start confirmations for an AI Service (task 1164), shared by
 * the AI Service page and the AI Services table's row menu (task 1191).
 *
 * Both are the app's `ConfirmDialog`, never `DeleteResourceControl`: nothing
 * is deleted, and a stop is undone by Start. Stop asks for an optional reason
 * and says what will happen; Start says Schedules stay paused, because the
 * owner resumes those deliberately (report, "Schedules"). Start is the label
 * the User sees (task 1191); the route, the hook and the dialog kind keep the
 * name `resume`.
 *
 * The dialog owns the mutation, the toast and the close. A failure shows the
 * service's own refusal (the task-858 rule) and leaves the dialog open, so the
 * typed reason is not lost.
 */
import { useEffect, useState } from 'react';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { UserName } from '@/components/ui/UserName';
import { useOptionalToast } from '@/contexts/ToastContext';
import { useResumeEndpoint, useStopEndpoint } from '@/hooks/useEndpoints';
import { refusalReason } from '@/utils/apiRefusal';
import { endpointDisplayName } from '@/utils/endpointLabel';
import { useFlag } from '@/hooks/useFeatureFlags';
import type { Endpoint } from '@/types';
import { formatStopInstant, NO_STOP_REASON } from './serviceStop';

export type ServiceStopDialogKind = 'stop' | 'resume';

/** The service's cap on a stop reason (task 1163: `reason` max 2000 characters). */
export const STOP_REASON_MAX_LENGTH = 2000;

interface ServiceStopDialogsProps {
  endpoint: Endpoint;
  open: ServiceStopDialogKind | null;
  onClose: () => void;
}

export function ServiceStopDialogs(props: ServiceStopDialogsProps) {
  // Stop and Start's lever (task 1216): off, no confirmation can open.
  const stopEnabled = useFlag('innovate.fe.services.stop');
  return stopEnabled ? <ServiceStopDialogsContent {...props} /> : null;
}

function ServiceStopDialogsContent({ endpoint, open, onClose }: ServiceStopDialogsProps) {
  const toast = useOptionalToast();
  const stopEndpoint = useStopEndpoint();
  const resumeEndpoint = useResumeEndpoint();
  const [reason, setReason] = useState('');
  const name = endpointDisplayName(endpoint);
  const stop = endpoint.stop ?? null;

  // A fresh Stop dialog starts with an empty reason.
  useEffect(() => {
    if (open === 'stop') setReason('');
  }, [open]);

  const confirmStop = async () => {
    try {
      await stopEndpoint.mutateAsync({ id: endpoint.id, reason: reason.trim() });
      toast?.success(`Stopped ${name}`, 'Calls are refused until you start it again.');
      onClose();
    } catch (error) {
      toast?.error('Could not stop this AI Service', refusalReason(error));
    }
  };

  const confirmResume = async () => {
    try {
      await resumeEndpoint.mutateAsync(endpoint.id);
      toast?.success(`Started ${name}`, 'Its Schedules stay paused until you resume them.');
      onClose();
    } catch (error) {
      toast?.error('Could not start this AI Service', refusalReason(error));
    }
  };

  return (
    <>
      <ConfirmDialog
        isOpen={open === 'stop'}
        onClose={onClose}
        onConfirm={confirmStop}
        title={`Stop ${name}?`}
        confirmLabel="Stop service"
        confirmStyle="danger"
        isLoading={stopEndpoint.isPending}
        message={
          <div data-testid="service-stop-dialog" className="space-y-3">
            <p>The service stays exactly as it is, but stops taking calls until you start it again.</p>
            <div>
              <label
                htmlFor="service-stop-reason"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Why are you stopping this service? (optional)
              </label>
              <textarea
                id="service-stop-reason"
                data-testid="service-stop-reason"
                rows={2}
                maxLength={STOP_REASON_MAX_LENGTH}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                disabled={stopEndpoint.isPending}
                className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Your team sees the reason on this page. Callers never do.
              </p>
            </div>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                Outside calls are refused with <code className="font-mono">409 service_stopped</code>.
                Your team can still use it in the app, including the Interface tab.
              </li>
              <li>Its Schedules pause, and stay paused after you start it.</li>
              <li>Configuration, URL, versions and history are unchanged. Tests still work.</li>
            </ul>
          </div>
        }
      />

      <ConfirmDialog
        isOpen={open === 'resume'}
        onClose={onClose}
        onConfirm={confirmResume}
        title={`Start ${name}?`}
        confirmLabel="Start service"
        confirmStyle="primary"
        isLoading={resumeEndpoint.isPending}
        message={
          <div data-testid="service-resume-dialog" className="space-y-3">
            <p>
              Calls are accepted again on the next request. The stop stays in the history with the
              time and your name.
            </p>
            {stop && stop.kind === 'manual' && (
              <p className="text-gray-600 dark:text-gray-400">
                Stopped by <UserName userId={stop.stoppedBy} /> on {formatStopInstant(stop.stoppedAt)}:{' '}
                {stop.reason?.trim() ? `“${stop.reason.trim()}”` : NO_STOP_REASON}
              </p>
            )}
            {stop && stop.kind === 'auto' && (
              <p className="text-gray-600 dark:text-gray-400">
                The failure count starts from zero. If the next calls keep failing, the service
                stops itself again.
              </p>
            )}
            <p data-testid="service-resume-schedules-note" className="font-medium">
              Schedules stay paused after you start the service. Resume each one under Config ›
              Schedules when you are ready.
            </p>
          </div>
        }
      />
    </>
  );
}

export default ServiceStopDialogs;
